import { forwardRef, useCallback } from 'react'
import { memo } from 'react';
import { useMemo } from 'react';
import { useState, useEffect, useRef } from 'react'
import { load, saveMergeObject } from '../../Utils/StorageIDB';

import "./ImageLoadIfVisible.scss";

export default memo(forwardRef(function ImageLoadIfVisible({ id, alt = "", className = "", style, src, defaultWidth, defaultHeight, ...props }, ref) {

    const sizeId = id ?? src;

    const [visible, setVisible] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [imageSizes, setImagesSizes] = useState({});

    const _internalRef = useRef(null);
    const elemRef = ref ?? _internalRef;

    const handleImageLoad = useCallback((e) => {
        setLoaded(true);

        const realSize = {
            width: e.target.naturalWidth,
            height: e.target.naturalHeight,
        };
        saveMergeObject('imageSizes', {
            [sizeId]: realSize,
        });
    }, [sizeId]);
    
    const imageStyle = useMemo(() => {
        const preloadSize = imageSizes[sizeId] ?? {
            width: defaultWidth ?? "100",
            height: defaultHeight ?? "100",
        };
        return {
            aspectRatio: `${preloadSize.width} / ${preloadSize.height}`,
        }
    }, [defaultHeight, defaultWidth, imageSizes, sizeId]);

    useEffect(() => {
        load('imageSizes')
            .then((imageSizes) => {
                if(!imageSizes) return;
                
                setImagesSizes(imageSizes)
            })
    }, []);

    useEffect(() => {

        let observer;

        if (window.IntersectionObserver) {
            observer = new IntersectionObserver( entries => {
                entries.forEach(entry => {
                    if ( entry.intersectionRatio > 0 || entry.isIntersecting ) {
                        setVisible(true)
                        observer.unobserve(entry.target)
                    }
                })
            }, {
                root: null,
                rootMargin: '0px',
                threshold: 0.001,
            })

            if (elemRef.current) {
                observer.observe(elemRef.current);
            }
        } 
        else {
            /* Old browsers fallback */
            setVisible(true)
        }

        elemRef.current.addEventListener("load", handleImageLoad);
        
        // store elem ref value as it could change by the time the clean up runs
        const elemRefCurrentCopy = elemRef.current;
        return () => {
            elemRefCurrentCopy.addEventListener("load", handleImageLoad);
            /* on component cleanup, we remove the listener */
            if (observer && observer.unobserve && elemRefCurrentCopy) {
                observer.unobserve(elemRefCurrentCopy)
            }
        }
    }, [elemRef, handleImageLoad])

    const displayProps = useMemo(() => ({
        ...props,
        className: `ImageLoadIfVisible ${visible ? 'ImageLoadIfVisible--visible' : 'ImageLoadIfVisible--notvisible'} ${loaded ? 'ImageLoadIfVisible--loaded' : 'ImageLoadIfVisible--notloaded'} ${className}`,
        ref: elemRef,
        src: visible ? src : null,
        style: {
            ...(!loaded ? imageStyle : {}),
            ...style,
        }
    }), [props, visible, loaded, className, elemRef, src, imageStyle, style]);

    return (
        <img {...displayProps} alt={alt} />
    );
}));